import {
  type AddOnCalc,
  type AddOnCalcMethod,
  type AddOnItem,
  type AddOnRule,
  type EditorAddOn,
  type EditorRoom,
  type EditorSurface,
  type Subfloor,
  type SubfloorCondition,
} from "./document"
import { holeMetrics, netPolygon } from "./geometry"
import { addOnItemLabel } from "./labels"
import { type PlacedImage, resolveSubfloor } from "./schedule"

// The add-on quantity engine. Pure and dependency-free (vitest-covered): the
// editor panel, the printed worksheet, and the schedule all compute from this
// one module, so a quantity can never disagree with itself across surfaces.
//
// The design brief in one line: if a line's quantity is a pure function of
// geometry + product + subfloor, the estimator never types it — and when the
// function CAN'T produce a number (wrong unit, no holes, filtered subfloor),
// it says so out loud instead of contributing a silent zero. Both documented
// incumbent failure modes (advisory-only unit compatibility, silent no-op
// methods) are the things this module exists to beat.

/**
 * SF and SY measure area, LF measures length, and everything else — EA, RL,
 * GAL, BAG, CTN, TUBE, PAIL — is a discrete "each" you buy whole. The
 * replica's live unit vocabulary is exactly SF · SY · LF · EA · RL (surveyed
 * 2026-08-10); manual items may bring the rest. Unknown (blank) is null:
 * computed with a warning, never blocked and never silently guessed.
 */
export type UnitClass = "area" | "linear" | "each"

/** Units offered for MANUAL add-on items — the replica's vocabulary plus the containers adhesives ship in. */
export const ADDON_UNITS = ["EA", "SF", "SY", "LF", "RL", "CTN", "GAL", "BAG", "TUBE", "PAIL"] as const

export function unitClass(unit: string | null | undefined): UnitClass | null {
  const u = unit?.trim().toUpperCase()
  if (!u) return null
  if (u === "SF" || u === "SY") return "area"
  if (u === "LF") return "linear"
  return "each"
}

/** What a method MEASURES (count and manual are already in item units). */
export function methodBasis(method: AddOnCalcMethod): UnitClass {
  switch (method) {
    case "room_area":
    case "hole_area":
      return "area"
    case "room_perimeter":
    case "hole_perimeter":
      return "linear"
    default:
      return "each"
  }
}

/**
 * The selection-time contract (never silent wrong numbers):
 *
 * | item unit ↓ / method → | area methods    | perimeter methods | count   | manual |
 * | area (SF/SY)           | ok (SY = SF/9)  | blocked           | blocked | ok     |
 * | linear (LF)            | blocked         | ok                | blocked | ok     |
 * | each (EA/GAL/…)        | needs coverage  | needs coverage    | ok      | ok     |
 * | unknown (blank)        | warn            | warn              | warn    | ok     |
 *
 * "blocked" means no coverage rate can save it — a linear-unit product on an
 * area method has no honest conversion (coverage converts a measured basis
 * into EACH units, not into a different measure). "needs_coverage" becomes ok
 * the moment a rate is present. "warn" computes but badges the line.
 */
export type Compatibility = "ok" | "needs_coverage" | "blocked" | "warn"

export function methodCompatibility(
  unit: string | null | undefined,
  method: AddOnCalcMethod,
  hasCoverage: boolean
): Compatibility {
  if (method === "manual") return "ok"
  const cls = unitClass(unit)
  if (cls == null) return "warn"
  if (method === "count") return cls === "each" ? "ok" : "blocked"
  const basis = methodBasis(method)
  if (cls === "each") return hasCoverage ? "ok" : "needs_coverage"
  return cls === basis ? "ok" : "blocked"
}

/**
 * A room reduced to the figures add-ons compute from, with the subfloor
 * cascade already resolved. Built by `roomFigures` — one definition serving
 * the editor panel and the printed pages identically.
 */
export type RoomFigures = {
  id: string
  name: string
  sheetId: number
  productId: string | null
  productType: string | null
  multiplier: number
  /** Per copy, null on an uncalibrated sheet — flagged, never dropped. */
  netSf: number | null
  perimFt: number | null
  holeSf: number | null
  holePerimFt: number | null
  /** Interior holes only — a deduction straddling a wall folds into the outline. */
  holeCount: number
  subfloor: Subfloor
}

export type SurfaceFigures = {
  id: string
  name: string
  productId: string | null
  productUnit: string | null
  /** The typed order quantity, in the product's own unit. */
  manualQty: number | null
  subfloor: Subfloor
}

export function roomFigures(
  room: EditorRoom,
  ftPerPx: number | null,
  images: PlacedImage[],
  projectDefault: Subfloor
): RoomFigures {
  const holes = ftPerPx != null ? holeMetrics(room.outline, room.holes) : null
  const net = ftPerPx != null ? netPolygon(room.outline, room.holes) : null
  return {
    id: room.id,
    name: room.name,
    sheetId: room.sheetId,
    productId: room.product?.id ?? null,
    productType: room.product?.product_type ?? null,
    multiplier: Math.max(room.multiplier, 1),
    netSf: net && ftPerPx != null ? net.areaPx2 * ftPerPx * ftPerPx : null,
    perimFt: net && ftPerPx != null ? net.outerPerimPx * ftPerPx : null,
    holeSf: holes && ftPerPx != null ? holes.areaPx2 * ftPerPx * ftPerPx : null,
    holePerimFt: holes && ftPerPx != null ? holes.perimPx * ftPerPx : null,
    holeCount: holeMetrics(room.outline, room.holes).count,
    subfloor: resolveSubfloor(room, images, projectDefault),
  }
}

/** Surfaces sit on no sheet, so their cascade is override -> project default. */
export function surfaceFigures(surface: EditorSurface, projectDefault: Subfloor): SurfaceFigures {
  return {
    id: surface.id,
    name: surface.name,
    productId: surface.product?.id ?? null,
    productUnit: surface.product?.unit ?? null,
    manualQty: surface.manualQty,
    subfloor: surface.subfloor ?? projectDefault,
  }
}

export type ZeroReason =
  | "no_holes"
  | "subfloor_filtered"
  | "type_filtered"
  | "zero_basis"
  | "no_geometry"

export type AddOnIssue =
  /** Unit ↔ method mismatch — qty is null, never a silent wrong number. */
  | { kind: "incompatible"; needsCoverage: boolean }
  /** Item has no unit — computed anyway, badged so someone fixes the item. */
  | { kind: "unknown_unit" }

export type AddOnContribution = {
  /** Null for a flat sheet-wide count — no single row produced it. */
  rowId: string | null
  label: string
  /** Measured basis (SF or LF, multiplier applied) — null for count/manual. */
  basis: number | null
  /** This row's fractional share, pre-min/pre-ceil. */
  qty: number | null
}

export type ComputedAddOn = {
  key: string // "addon:<client_id>" | "rule:<id>"
  source: { kind: "addon"; addOn: EditorAddOn } | { kind: "rule"; rule: AddOnRule }
  scope: "room" | "surface" | "sheet" | "product"
  item: AddOnItem
  calc: AddOnCalc
  subfloorCondition: SubfloorCondition | null
  printOnSchedule: boolean
  unit: string | null
  unitClass: UnitClass | null
  /** The ordering number: coverage → min → ceil. Null when incompatible. */
  qty: number | null
  /** Pre-min, pre-ceil — shown so the rounding is auditable. */
  rawQty: number | null
  minApplied: boolean
  contributions: AddOnContribution[]
  zeroRows: { rowId: string | null; label: string; reason: ZeroReason }[]
  /** Rows on uncalibrated sheets — flagged, never silently dropped. */
  unmeasured: string[]
}

/** A usable coverage rate is strictly positive — 0 would divide the basis into Infinity. */
function hasCoverage(calc: AddOnCalc): boolean {
  return calc.coverage_rate != null && calc.coverage_rate > 0
}

export function computedIssue(computed: ComputedAddOn): AddOnIssue | null {
  const compat = methodCompatibility(
    computed.unit,
    computed.calc.calc_method,
    hasCoverage(computed.calc)
  )
  if (compat === "blocked" || compat === "needs_coverage") {
    return { kind: "incompatible", needsCoverage: compat === "needs_coverage" }
  }
  if (compat === "warn") return { kind: "unknown_unit" }
  return null
}

export type ComputeAddOnsArgs = {
  addOns: EditorAddOn[]
  rules: AddOnRule[]
  rooms: RoomFigures[]
  surfaces: SurfaceFigures[]
}

/**
 * Every add-on and every rule, computed. Policy, in the order it applies:
 *
 * - The basis SUMS `figure × multiplier` across the target rows, and
 *   coverage divides the SUM — per-row rounding would systematically overbuy
 *   (the carton precedent in rollup.ts).
 * - `count` is N per target ROW (× multiplier) — "2 wax rings per bathroom".
 *   On a sheet or surface scope there is exactly one target, so it reads as
 *   a flat count.
 * - `manual` is the estimator's typed TOTAL — never multiplied, never
 *   summed; it exists so a quantity with no geometric basis still totals.
 * - min_qty applies only when the raw result is positive: a zero means the
 *   precondition is absent (no holes ⇒ no hole sealant), and a minimum must
 *   not resurrect it.
 * - Ceil applies to each-class results only — you buy 3 pails, not 2.25,
 *   but 187.4 SF of pad stays fractional.
 * - A subfloor condition filters target rows by their RESOLVED subfloor;
 *   "unspecified" matches nothing (visible zero beats confident wrong).
 * - Incompatible unit ↔ method ⇒ qty null + issue. NEVER a silent 0.
 */
export function computeAddOns(args: ComputeAddOnsArgs): ComputedAddOn[] {
  const out: ComputedAddOn[] = []

  for (const addOn of args.addOns) {
    const scope: ComputedAddOn["scope"] = addOn.parentRoomId
      ? "room"
      : addOn.parentSurfaceId
        ? "surface"
        : "sheet"
    out.push(
      computeOne({
        key: `addon:${addOn.id}`,
        source: { kind: "addon", addOn },
        scope,
        item: addOn.item,
        calc: addOn,
        subfloorCondition: addOn.subfloorCondition,
        productTypes: scope === "sheet" ? addOn.productTypes : null,
        printOnSchedule: addOn.printOnSchedule,
        targetRooms:
          scope === "room"
            ? args.rooms.filter((room) => room.id === addOn.parentRoomId)
            : scope === "sheet"
              ? args.rooms.filter((room) => room.sheetId === addOn.sheetId)
              : [],
        targetSurfaces:
          scope === "surface"
            ? args.surfaces.filter((surface) => surface.id === addOn.parentSurfaceId)
            : [],
      })
    )
  }

  for (const rule of args.rules) {
    out.push(
      computeOne({
        key: `rule:${rule.id}`,
        source: { kind: "rule", rule },
        scope: "product",
        item: rule.item,
        calc: rule,
        subfloorCondition: rule.subfloorCondition,
        productTypes: null,
        printOnSchedule: rule.printOnSchedule,
        targetRooms: args.rooms.filter((room) => room.productId === rule.productId),
        targetSurfaces: args.surfaces.filter((surface) => surface.productId === rule.productId),
      })
    )
  }

  return out
}

type ComputeOneArgs = {
  key: string
  source: ComputedAddOn["source"]
  scope: ComputedAddOn["scope"]
  item: AddOnItem
  calc: AddOnCalc
  subfloorCondition: SubfloorCondition | null
  /** Sheet scope only — gate rooms by their product's type. */
  productTypes: string[] | null
  printOnSchedule: boolean
  targetRooms: RoomFigures[]
  targetSurfaces: SurfaceFigures[]
}

function computeOne(args: ComputeOneArgs): ComputedAddOn {
  const { calc, item } = args
  const unit = item.unit ?? null
  const cls = unitClass(unit)
  const compat = methodCompatibility(unit, calc.calc_method, hasCoverage(calc))

  const base: Omit<ComputedAddOn, "qty" | "rawQty" | "minApplied"> = {
    key: args.key,
    source: args.source,
    scope: args.scope,
    item,
    calc: {
      calc_method: calc.calc_method,
      coverage_rate: calc.coverage_rate,
      min_qty: calc.min_qty,
      count_qty: calc.count_qty,
      manual_qty: calc.manual_qty,
    },
    subfloorCondition: args.subfloorCondition,
    printOnSchedule: args.printOnSchedule,
    unit,
    unitClass: cls,
    contributions: [],
    zeroRows: [],
    unmeasured: [],
  }

  if (compat === "blocked" || compat === "needs_coverage") {
    return { ...base, qty: null, rawQty: null, minApplied: false }
  }

  // The typed total: no targets, no filters, no rounding surprises. (Rules
  // never carry it — the model forbids manual on a rule.)
  if (calc.calc_method === "manual") {
    return {
      ...base,
      qty: calc.manual_qty,
      rawQty: calc.manual_qty,
      minApplied: false,
    }
  }

  const contributions: AddOnContribution[] = []
  const zeroRows: ComputedAddOn["zeroRows"] = []
  const unmeasured: string[] = []

  const typeFilter = args.productTypes?.length ? new Set(args.productTypes) : null
  const roomsAfterCondition: RoomFigures[] = []
  for (const room of args.targetRooms) {
    // Type filter first — an unassigned room (no product) matches no list,
    // and the visible zero says why instead of quietly shrinking the total.
    if (typeFilter && !(room.productType && typeFilter.has(room.productType))) {
      zeroRows.push({ rowId: room.id, label: room.name, reason: "type_filtered" })
    } else if (args.subfloorCondition && room.subfloor !== args.subfloorCondition) {
      zeroRows.push({ rowId: room.id, label: room.name, reason: "subfloor_filtered" })
    } else {
      roomsAfterCondition.push(room)
    }
  }
  const surfacesAfterCondition: SurfaceFigures[] = []
  for (const surface of args.targetSurfaces) {
    if (args.subfloorCondition && surface.subfloor !== args.subfloorCondition) {
      zeroRows.push({ rowId: surface.id, label: surface.name, reason: "subfloor_filtered" })
    } else {
      surfacesAfterCondition.push(surface)
    }
  }

  // `count` is N per matching row everywhere — room ×multiplier, surface
  // flat, and on sheet scope N per room that survives the type and subfloor
  // filters ("1 transition per LVP room on this floor"). A flat sheet-wide
  // number is what `manual` is for. (Changed from flat during the M8
  // constraints follow-up, 2026-08-10 — per-row is what the filters imply
  // and matches count's meaning at every other scope.)
  if (calc.calc_method === "count") {
    const per = calc.count_qty ?? 0
    for (const room of roomsAfterCondition) {
      const qty = per * room.multiplier
      if (qty > 0) contributions.push({ rowId: room.id, label: room.name, basis: null, qty })
      else zeroRows.push({ rowId: room.id, label: room.name, reason: "zero_basis" })
    }
    for (const surface of surfacesAfterCondition) {
      if (per > 0)
        contributions.push({ rowId: surface.id, label: surface.name, basis: null, qty: per })
      else zeroRows.push({ rowId: surface.id, label: surface.name, reason: "zero_basis" })
    }
    const raw = contributions.reduce((sum, c) => sum + (c.qty ?? 0), 0)
    return finish(base, raw, contributions, zeroRows, unmeasured, calc)
  }

  // The four measuring methods. Each room contributes figure × multiplier;
  // a surface has no geometry — its typed quantity stands in as an AREA
  // basis when its product's unit measures area, and every other pairing is
  // an honest "no geometry" zero (the UI disables those methods up front).
  for (const room of roomsAfterCondition) {
    const figure = roomBasis(room, calc.calc_method)
    if (figure == null) {
      unmeasured.push(room.name)
      continue
    }
    if (figure > 0) {
      contributions.push({
        rowId: room.id,
        label: room.name,
        basis: figure,
        qty: null, // filled below once the conversion is known
      })
    } else {
      const holeMethod = calc.calc_method === "hole_area" || calc.calc_method === "hole_perimeter"
      zeroRows.push({
        rowId: room.id,
        label: room.name,
        reason: holeMethod && room.holeCount === 0 ? "no_holes" : "zero_basis",
      })
    }
  }
  for (const surface of surfacesAfterCondition) {
    if (
      calc.calc_method === "room_area" &&
      surface.manualQty != null &&
      unitClass(surface.productUnit) === "area"
    ) {
      // Typed in the product's unit — normalize SY back to SF for the basis.
      const sf =
        surface.productUnit?.trim().toUpperCase() === "SY"
          ? surface.manualQty * 9
          : surface.manualQty
      if (sf > 0) contributions.push({ rowId: surface.id, label: surface.name, basis: sf, qty: null })
      else zeroRows.push({ rowId: surface.id, label: surface.name, reason: "zero_basis" })
    } else {
      zeroRows.push({ rowId: surface.id, label: surface.name, reason: "no_geometry" })
    }
  }

  const totalBasis = contributions.reduce((sum, c) => sum + (c.basis ?? 0), 0)
  const convert = (basis: number): number => {
    if (hasCoverage(calc)) return basis / calc.coverage_rate!
    // Direct: the unit measures what the method measures (or is unknown —
    // computed in basis units, badged by the caller via computedIssue).
    if (cls === "area" && unit?.trim().toUpperCase() === "SY") return basis / 9
    return basis
  }
  for (const c of contributions) c.qty = c.basis != null ? convert(c.basis) : c.qty
  return finish(base, convert(totalBasis), contributions, zeroRows, unmeasured, calc)
}

function roomBasis(room: RoomFigures, method: AddOnCalcMethod): number | null {
  switch (method) {
    case "room_area":
      return room.netSf == null ? null : room.netSf * room.multiplier
    case "room_perimeter":
      return room.perimFt == null ? null : room.perimFt * room.multiplier
    case "hole_area":
      return room.holeSf == null ? null : room.holeSf * room.multiplier
    case "hole_perimeter":
      return room.holePerimFt == null ? null : room.holePerimFt * room.multiplier
    default:
      return null
  }
}

function finish(
  base: Omit<ComputedAddOn, "qty" | "rawQty" | "minApplied">,
  raw: number,
  contributions: AddOnContribution[],
  zeroRows: ComputedAddOn["zeroRows"],
  unmeasured: string[],
  calc: AddOnCalc
): ComputedAddOn {
  // min only when raw > 0 (never resurrect an absent precondition), then
  // ceil for each-class results (and unknown units under a coverage rate,
  // which are each-shaped by construction).
  const minApplied = raw > 0 && calc.min_qty != null && calc.min_qty > raw
  const afterMin = minApplied ? calc.min_qty! : raw
  const eachLike = base.unitClass === "each" || (base.unitClass == null && hasCoverage(calc))
  // Math.max guards the -0 that ceil(0 − ε) produces — "-0 EA" is not a line
  // anyone should read on a purchasing document.
  const qty = eachLike ? Math.max(0, Math.ceil(afterMin - 1e-9)) : afterMin
  return { ...base, qty, rawQty: raw, minApplied, contributions, zeroRows, unmeasured }
}

/** What the schedule prints under one row, gated and worded. */
export type ScheduleAddOnLine = {
  key: string
  label: string
  /** Rule-derived — attached to the product, not placed by hand on this row. */
  auto: boolean
  item: AddOnItem
  condition: SubfloorCondition | null
}

/**
 * Row id -> the add-on lines that print under it on the material schedule.
 * Sheet-scope lines land under a synthetic `sheet:<id>` key for the block
 * after the table. Two gates: print_on_schedule, and the per-row subfloor
 * condition — a rule filtered out of a room must not print under it (the
 * schedule is installer instructions; printing glue on a wood-subfloor room
 * is exactly the wrong-answer document M8 exists to prevent).
 *
 * NO QUANTITIES — the M6 charter stands: the schedule answers "what goes
 * where", the worksheet answers "how much".
 */
export function scheduleAddOnsByRow(
  addOns: EditorAddOn[],
  rules: AddOnRule[],
  rooms: RoomFigures[],
  surfaces: SurfaceFigures[]
): Map<string, ScheduleAddOnLine[]> {
  const out = new Map<string, ScheduleAddOnLine[]>()
  const push = (rowKey: string, line: ScheduleAddOnLine) => {
    const list = out.get(rowKey)
    if (list) list.push(line)
    else out.set(rowKey, [line])
  }
  const roomsById = new Map(rooms.map((room) => [room.id, room]))
  const surfacesById = new Map(surfaces.map((surface) => [surface.id, surface]))
  const matches = (
    condition: SubfloorCondition | null,
    row: { subfloor: Subfloor } | undefined
  ): boolean => !condition || row?.subfloor === condition

  for (const addOn of addOns) {
    if (!addOn.printOnSchedule) continue
    const line: ScheduleAddOnLine = {
      key: `addon:${addOn.id}`,
      label: addOnItemLabel(addOn.item),
      auto: false,
      item: addOn.item,
      condition: addOn.subfloorCondition,
    }
    if (addOn.parentRoomId) {
      if (matches(addOn.subfloorCondition, roomsById.get(addOn.parentRoomId)))
        push(addOn.parentRoomId, line)
    } else if (addOn.parentSurfaceId) {
      if (matches(addOn.subfloorCondition, surfacesById.get(addOn.parentSurfaceId)))
        push(addOn.parentSurfaceId, line)
    } else if (addOn.sheetId != null) {
      push(`sheet:${addOn.sheetId}`, line)
    }
  }

  for (const rule of rules) {
    if (!rule.printOnSchedule) continue
    const line: ScheduleAddOnLine = {
      key: `rule:${rule.id}`,
      label: addOnItemLabel(rule.item),
      auto: true,
      item: rule.item,
      condition: rule.subfloorCondition,
    }
    for (const room of rooms) {
      if (room.productId === rule.productId && matches(rule.subfloorCondition, room))
        push(room.id, { ...line, key: `${line.key}:${room.id}` })
    }
    for (const surface of surfaces) {
      if (surface.productId === rule.productId && matches(rule.subfloorCondition, surface))
        push(surface.id, { ...line, key: `${line.key}:${surface.id}` })
    }
  }

  return out
}
